import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiKeyFormDialog } from '@/components/ApiKeyFormDialog';
import { ApiKeyActivityDialog } from '@/components/audit/ApiKeyActivityDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SecretRevealDialog, type RevealedSecret } from '@/components/SecretRevealDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatAgo } from '@/lib/ago';
import { api } from '@/lib/api';
import { ruleBadges } from '@/lib/apiKeyRules';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { APIKey, CreateAPIKeyResponse } from '@/types/apiKey';

/** Builds the one-time reveal for a plaintext key the server returned on create or regenerate. */
function revealKey({ id, name, key }: { id: number; name: string; key: string }, regenerated: boolean): RevealedSecret {
  return {
    id,
    title: regenerated ? 'API key regenerated' : 'API key created',
    description: `Copy '${name}' now. It will not be shown again.`,
    label: 'API key',
    value: key,
    warning:
      'If you lose this key you must regenerate it, which invalidates the old one.' +
      (regenerated ? ' The previous key no longer works.' : ''),
  };
}

type PendingAction = { kind: 'delete' | 'regenerate'; key: APIKey };

function formatTime(iso: string | null): { ago: string; full: string } | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

function TimeCell({ iso, empty }: { iso: string | null; empty: string }) {
  const time = formatTime(iso);
  if (!time) return <span className="text-muted-foreground">{empty}</span>;
  return (
    <time dateTime={iso ?? undefined} title={time.full}>
      {time.ago}
    </time>
  );
}

export function ApiKeyList() {
  const keys = usePolledData(() => api.apiKeys.list());
  // `editing` (null = create) keeps the key through the form's exit animation; `formOpen` controls visibility.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<APIKey | null>(null);
  const [reveal, setReveal] = useState<RevealedSecret | null>(null);
  const [activityKeyId, setActivityKeyId] = useState<number | null>(null);
  // `pending` keeps the target through the dialog's exit animation; `pendingOpen` controls visibility.
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const busyRef = useRef(false);

  const openPending = (action: PendingAction) => {
    setActionError(null);
    setPending(action);
    setPendingOpen(true);
  };

  const closePending = () => {
    if (busyRef.current) return;
    setPendingOpen(false);
    setActionError(null);
  };

  const runPending = async () => {
    const target = pending;
    if (!target || busyRef.current) return;
    busyRef.current = true;
    setActionBusy(true);
    setActionError(null);
    try {
      if (target.kind === 'delete') {
        await api.apiKeys.delete(target.key.id);
        setPendingOpen(false);
        toast.success('API key deleted');
      } else {
        const data = await api.apiKeys.regenerate(target.key.id);
        setPendingOpen(false);
        setReveal(revealKey(data, true));
      }
      keys.refetch();
    } catch (err) {
      setActionError(err instanceof Error && err.message ? err.message : `Failed to ${target.kind} API key`);
    } finally {
      busyRef.current = false;
      setActionBusy(false);
    }
  };

  const openForm = (key: APIKey | null) => {
    setEditing(key);
    setFormOpen(true);
  };

  const handleCreated = (created: CreateAPIKeyResponse) => {
    setFormOpen(false);
    setReveal(revealKey(created, false));
    keys.refetch();
  };

  const handleSaved = () => {
    setFormOpen(false);
    keys.refetch();
  };

  const rows = keys.data ?? [];
  const pendingName = pending?.key.name ?? '';
  const isDelete = pending?.kind === 'delete';

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>Keys</CardTitle>
              <CardDescription>Keys the external SaaS uses to authenticate. The full key is shown once, on create or regenerate.</CardDescription>
            </div>
            <Button onClick={() => openForm(null)}>Create API key</Button>
          </div>
        </CardHeader>
        <CardContent data-focus-fallback tabIndex={-1} className="focus:outline-none">
          {keys.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading API keys…
            </p>
          ) : keys.data === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{keys.error ?? 'Failed to load API keys'}</span>
              <Button size="sm" variant="outline" onClick={keys.refetch}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No API keys yet. Create one for the external SaaS to use.
            </div>
          ) : (
            <>
              {keys.error ? (
                <p role="alert" className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh API keys: {keys.error}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Rules</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell className="font-medium [overflow-wrap:anywhere]">{key.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {ruleBadges(key).map((label) => (
                            <Badge key={label} variant="secondary">
                              {label}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={key.is_active ? 'active' : 'revoked'} />
                      </TableCell>
                      <TableCell className="text-sm">
                        <TimeCell iso={key.expires_at} empty="Never" />
                      </TableCell>
                      <TableCell className="text-sm">
                        <TimeCell iso={key.created_at} empty="-" />
                      </TableCell>
                      <TableCell className="text-sm">
                        <TimeCell iso={key.last_used_at} empty="Never used" />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button size="sm" variant="outline" aria-label={`Edit rules for ${key.name}`} onClick={() => openForm(key)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" aria-label={`View activity for ${key.name}`} onClick={() => setActivityKeyId(key.id)}>
                            Activity
                          </Button>
                          <Button size="sm" variant="outline" aria-label={`Regenerate ${key.name}`} onClick={() => openPending({ kind: 'regenerate', key })}>
                            Regenerate
                          </Button>
                          <Button size="sm" variant="destructive" aria-label={`Delete ${key.name}`} onClick={() => openPending({ kind: 'delete', key })}>
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <ApiKeyActivityDialog keyId={activityKeyId} onClose={() => setActivityKeyId(null)} />

      <ApiKeyFormDialog open={formOpen} editing={editing} onOpenChange={setFormOpen} onCreated={handleCreated} onSaved={handleSaved} />

      <SecretRevealDialog reveal={reveal} onDone={() => setReveal(null)} />

      <ConfirmDialog
        open={pendingOpen}
        onOpenChange={(next) => {
          if (!next) closePending();
        }}
        title={isDelete ? 'Delete API key?' : 'Regenerate API key?'}
        description={
          isDelete
            ? `Delete '${pendingName}'? Any client still using it will be refused immediately.`
            : `Regenerate '${pendingName}'? The current key stops working immediately and a new key is issued.`
        }
        confirmLabel={isDelete ? (actionBusy ? 'Deleting…' : 'Delete') : actionBusy ? 'Regenerating…' : 'Regenerate'}
        destructive
        loading={actionBusy}
        error={actionError}
        onConfirm={() => void runPending()}
      />
    </>
  );
}
